import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Helmet } from "react-helmet-async";
import TranslateService from "../services/TranslateService";

type TextDirection = "ltr" | "rtl";

type I18nContextType = {
  lang: string;
  setLang: (lang: string) => Promise<void>;
  t: (key: string, params?: any) => string;
  direction: TextDirection;
  setDirection: (dir: TextDirection) => Promise<void>;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const directionFromLang = (l: string): TextDirection => {
  const code = (l || "").toLowerCase();
  return code.startsWith("ar") ||
    code.startsWith("fa") ||
    code.startsWith("he") ||
    code.startsWith("ur")
    ? "rtl"
    : "ltr";
};

// Language is driven by the /:lang URL segment (not localStorage), so that
// pre-rendered (react-snap) HTML is generated in the correct language and the
// first synchronous client render matches the snapshot (no hydration mismatch).
const getLangFromPath = (): string => {
  if (typeof window === "undefined") return "en";
  const seg = window.location.pathname.split("/")[1]?.toLowerCase();
  return seg === "ar" || seg === "en" ? seg : "en";
};

export const I18nProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const initialLang = getLangFromPath();
  const [lang, setLang] = useState<string>(initialLang);
  const [direction, setDirState] = useState<TextDirection>(
    directionFromLang(initialLang),
  );

  const setLanguage = useCallback(async (l: string) => {
    await TranslateService.setCurrentLanguage(l);
    setLang(l);
    const dir = directionFromLang(l);
    setDirState(dir);
    try {
      await TranslateService.setDirection(dir);
    } catch {}
  }, []);

  const setDirection = useCallback(async (dir: TextDirection) => {
    await TranslateService.setDirection(dir);
    setDirState(dir);
  }, []);

  const t = useCallback(
    (key: string, params?: any): string =>
      TranslateService.translateSync(key, params, lang),
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang: setLanguage, t, direction, setDirection }),
    [lang, setLanguage, t, direction, setDirection]
  );

  // Single source of truth for <html lang/dir>. Because I18nProvider renders on
  // every route, the attributes are always declared by exactly one Helmet — so
  // navigating to a page that renders no <SEO> can never strip them.
  return (
    <I18nContext.Provider value={value}>
      <Helmet htmlAttributes={{ lang, dir: direction }} />
      {children}
    </I18nContext.Provider>
  );
};

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
export function useTranslate() {
  return useI18n().t;
}
export type { TextDirection, I18nContextType };
